import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { Urls } from '@share/constants';
import { ReservationsAPI } from '@share/api';
import { AppThunk, axiosInstance, getHeaders, getSelectedCurrency } from '@share/utils';
import moment, { Moment } from 'moment';
import { ICard, IUser } from '../../components/pages/insurance/insurance-quote-modal';

// Interfaces for product details
export interface IProductDetails {
  productCode: string;
  productName: string;
  planCode: string;
  planDescription: string;
  price: number;
}

// Interface for policy holder's address
export interface IAddress {
  city: string;
  isoCountry: string;
  isoStateOrProvince: string;
  postalCode: string;
  street1: string;
  type: string;
}

// Interface for policy holder
export interface IPolicyHolder {
  insuredType: string;
  firstName: string;
  middleName: string;
  lastName: string;
  addresses: IAddress;
  email: string;
}

// Interface for checkout details
export interface ICheckoutResponse {
  effectiveDate: string;
  expirationDate: string;
  policyHolder: IPolicyHolder;
  policyLookup: string;
  policyNumber: string;
  policyStatus: string;
  productDetail: IProductDetails;
  totalPrice: number;
  loading: boolean;
  error: string;
}

// Interface for quote details
export interface IQuoteResponse {
  quoteId: string;
  productDetails: IProductDetails;
  loading: boolean;
  error: string;
}

/**
 * Policy holder details recovered from the booking a cross-sell link came from, used to
 * prefill the user form so the member does not retype what we already hold.
 */
export interface IBookingPolicyHolder {
  bookingNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  street: string;
  city: string;
  postalCode: string;
  email: string;
  phone: string;
}

/**
 * Identifiers of the booking a cross-sell link points at. Sent with the checkout so the
 * policy is attached to that booking instead of creating a standalone insurance booking.
 */
export interface IBookingLink {
  policyBookingId: string;
  bookingInternalId: number;
}

/**
 * Everything the quote needs, taken from the booking itself. Held as plain strings and
 * numbers so the store stays serializable; the page turns the dates back into Moments.
 */
export interface IBookingQuoteFilter {
  checkIn: string | null;
  checkOut: string | null;
  travelers: number | null;
  tripCost: number | null;
  state: string | null;
  /** Destination country as the booking spells it — an ISO code or a country name. */
  destination: string | null;
}

// Final interface for the entire insurance quote state
export interface IQuoteCheckoutResponse {
  quote: IQuoteResponse;
  checkout: ICheckoutResponse;
  bookingPolicyHolder: IBookingPolicyHolder | null;
  /** The quote filter derived from the linked booking. Authoritative over the query string. */
  bookingQuoteFilter: IBookingQuoteFilter | null;
  /** Set only when the quote came from a booking, so the policy can be linked to it. */
  bookingLink: IBookingLink | null;
  /** i18n key explaining why the linked booking cannot be insured, when it cannot. */
  bookingIneligibleReason: string | null;
}

export const insuranceQuoteInitialState: IQuoteCheckoutResponse = {
  quote: {
    quoteId: '',
    productDetails: {
      productCode: '',
      productName: '',
      planCode: '',
      planDescription: '',
      price: 0,
    },
    loading: false,
    error: '',
  },
  checkout: {
    effectiveDate: '',
    expirationDate: '',
    policyHolder: {
      insuredType: '',
      firstName: '',
      middleName: '',
      lastName: '',
      addresses: {
        city: '',
        isoCountry: '',
        isoStateOrProvince: '',
        postalCode: '',
        street1: '',
        type: '',
      },
      email: '',
    },
    policyLookup: '',
    policyNumber: '',
    policyStatus: '',
    productDetail: {
      productCode: '',
      productName: '',
      planCode: '',
      planDescription: '',
      price: 0,
    },
    totalPrice: 0,
    loading: false,
    error: '',
  },
  bookingPolicyHolder: null,
  bookingQuoteFilter: null,
  bookingLink: null,
  bookingIneligibleReason: null,
};

const insuranceQuoteSlice = createSlice({
  name: 'insuranceQuote',
  initialState: insuranceQuoteInitialState,
  reducers: {
    setQuote: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<{ quote: IQuoteResponse }>,
    ) => {
      state.quote = payload.quote;
    },
    setQuoteLoading: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<{ loading: boolean }>,
    ) => {
      state.quote.loading = payload.loading;
    },
    setQuoteError: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<{ error: string }>,
    ) => {
      state.quote.error = payload.error;
    },
    clearQuoteError: (state: IQuoteCheckoutResponse) => {
      state.quote.error = '';
    },
    setCheckoutLoading: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<{ loading: boolean }>,
    ) => {
      state.checkout.loading = payload.loading;
    },
    setCheckoutError: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<{ error: string }>,
    ) => {
      state.checkout.error = payload.error;
    },
    clearCheckoutError: (state: IQuoteCheckoutResponse) => {
      state.checkout.error = '';
    },
    setCheckout: (state: IQuoteCheckoutResponse, { payload }: PayloadAction<{ checkout: any }>) => {
      state.checkout = payload.checkout;
    },
    clearQuoteCheckout: (state: IQuoteCheckoutResponse) => {
      state.quote = insuranceQuoteInitialState.quote;
      state.checkout = insuranceQuoteInitialState.checkout;
    },
    setBookingPolicyHolder: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<IBookingPolicyHolder | null>,
    ) => {
      state.bookingPolicyHolder = payload;
    },
    setBookingLink: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<IBookingLink | null>,
    ) => {
      state.bookingLink = payload;
    },
    setBookingQuoteFilter: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<IBookingQuoteFilter | null>,
    ) => {
      state.bookingQuoteFilter = payload;
    },
    setBookingIneligibleReason: (
      state: IQuoteCheckoutResponse,
      { payload }: PayloadAction<string | null>,
    ) => {
      state.bookingIneligibleReason = payload;
    },
  },
});

export const insuranceQuoteActions = insuranceQuoteSlice.actions;

export const insuranceQuoteReducer = insuranceQuoteSlice.reducer;

/**
 * Recovers the policy holder details from the booking a cross-sell link points at, so the
 * checkout form arrives prefilled. The bookings endpoint only returns the signed-in
 * member's own reservations, so an id that is not theirs simply yields nothing.
 *
 * Best effort throughout: any failure leaves the form empty and manually fillable rather
 * than blocking the purchase.
 */
/** Only hotel and condo bookings can be insured, matching BookingTypeEnum.Hotel / .Condo. */
const INSURABLE_BOOKING_TYPES = ['hotel', 'condo'];
const US_COUNTRY = 'US';
/** Condo bookings store the country as "USA" where hotels store "US". */
const US_COUNTRY_ALIASES = ['US', 'USA', 'UNITED STATES'];

const normalizeCountry = (value?: string): string => {
  const country = (value || '').trim().toUpperCase();

  return US_COUNTRY_ALIASES.includes(country) ? US_COUNTRY : country;
};

const PHONE_MIN_DIGITS = 7;
const MIN_TRAVELERS = 1;
const MAX_TRAVELERS = 50;
const BOOKING_DATE_FORMATS = ['YYYY-M-D', 'YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601];

/** Booking dates come through as MM/DD/YYYY; the quote form wants ISO. */
const toIsoDate = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  const parsed = moment(value, BOOKING_DATE_FORMATS as any);

  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

/** Head count comes off the room occupancies, falling back to the listed guests. */
const countTravelers = (rooms: any[], guests: any[]): number | null => {
  const occupants = rooms.reduce((total: number, room: any) => {
    const adults = Number(room?.occupancy?.adults) || 0;
    const kids = room?.occupancy?.kidAges?.length || 0;

    return total + adults + kids;
  }, 0);

  const travelers = occupants || guests.length;

  return travelers >= MIN_TRAVELERS && travelers <= MAX_TRAVELERS ? travelers : null;
};

/**
 * Picks the first candidate that actually looks like a phone number. Bookings sometimes
 * carry only a country code on the guest record, which would otherwise win over the
 * usable number held on the reservation itself.
 */
const pickPhone = (...candidates: (string | undefined)[]): string =>
  candidates
    .map((candidate: string | undefined) => (candidate || '').replace(/\D/g, ''))
    .find((digits: string) => digits.length >= PHONE_MIN_DIGITS) || '';

export const GetBookingPolicyHolder = (bookingId: string): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(insuranceQuoteActions.setBookingIneligibleReason(null));
      dispatch(insuranceQuoteActions.setBookingLink(null));

      const { data: booking } = await ReservationsAPI.getBookingById(bookingId);

      if (!booking?.bookingId) {
        return;
      }

      // Guests hang off the rooms; the top-level `guests` is null on this endpoint.
      const guests = (booking?.room || []).map((room: any) => room?.guests || []).flat();
      const mainGuest = guests.find((guest: any) => guest?.isMainGuest) || guests[0];

      /* Only hotel and condo reservations are insurable, and only for US residents. A link
         naming anything else is rejected outright rather than quoted: nothing is prefilled,
         no trip cost is published, and the reason is surfaced to the member. */
      const bookingType = (booking?.bookingType || '').trim().toLowerCase();

      if (bookingType && !INSURABLE_BOOKING_TYPES.includes(bookingType)) {
        dispatch(
          insuranceQuoteActions.setBookingIneligibleReason('insurance.booking.not_insurable'),
        );

        return;
      }

      /* Condo bookings leave countryCode empty and record the country in residence ("USA"),
         so both are read. The signed-in member's own country is deliberately not consulted:
         it is where the account is registered, not where the traveller resides, and using it
         rejected US guests on every condo booking. An unknown country is still left alone —
         the provider turns down non-US quotes anyway. */
      const residencyCountry = normalizeCountry(mainGuest?.countryCode || mainGuest?.residence);

      if (residencyCountry && residencyCountry !== US_COUNTRY) {
        dispatch(
          insuranceQuoteActions.setBookingIneligibleReason('insurance.booking.not_us_resident'),
        );

        return;
      }

      /* Mirrors the in-checkout path (addInsuranceToReservation): the policy is attached to
         the booking by its guid plus internal id, otherwise the backend files it as a
         standalone insurance booking and the stay stays unprotected. */
      if (booking?.bookingInternalId) {
        dispatch(
          insuranceQuoteActions.setBookingLink({
            policyBookingId: booking.bookingId,
            bookingInternalId: booking.bookingInternalId,
          }),
        );
      }

      dispatch(
        insuranceQuoteActions.setBookingPolicyHolder({
          bookingNumber: booking?.providerConfirmationId || booking?.customerLocator || '',
          firstName: mainGuest?.givenName || '',
          middleName: mainGuest?.middleName || '',
          lastName: mainGuest?.surName || '',
          street: [mainGuest?.addressLine, mainGuest?.addressLine2]
            .filter((line?: string) => !isEmpty(line))
            .join(' '),
          city: mainGuest?.city || '',
          postalCode: mainGuest?.postalCode || '',
          email: mainGuest?.email || '',
          phone: pickPhone(mainGuest?.phone, booking?.phone),
        }),
      );

      /* The quote is built from the booking itself, never from the query string: every one
         of these values moves the premium and every one of them is editable in the address
         bar. A link that names a booking therefore ignores the other parameters entirely. */
      const rooms = booking?.room || [];
      const tripCost = Number(booking?.price);

      dispatch(
        insuranceQuoteActions.setBookingQuoteFilter({
          checkIn: toIsoDate(booking?.checkIn),
          checkOut: toIsoDate(booking?.checkOut),
          travelers: countTravelers(rooms, guests),
          tripCost: Number.isFinite(tripCost) && tripCost > 0 ? tripCost : null,
          state: (mainGuest?.stateCode || '').trim().toUpperCase() || null,
          /* The property's own country is the true destination, but it comes through empty
             on plenty of bookings — the main guest's country is the practical stand-in, and
             is what the checkout quote already fell back to. */
          destination:
            (booking?.property?.country || mainGuest?.countryCode || mainGuest?.residence || '')
              .trim() || null,
        }),
      );
    } catch (error: any) {
      // Prefill is a convenience; log and let the member fill the form themselves.
      console.error(error);
    }
  };
};

export const PostInsuranceQuote = (
  total: number | null,
  dates: [Moment | null, Moment | null] | null,
  destinationIsoCountryCode: string | null,
  isoStateOrProvince: string | null,
  numberOfTravelers: number | null,
): AppThunk => {
  return async (dispatch, getState) => {
    try {
      dispatch(insuranceQuoteActions.setQuoteLoading({ loading: true }));
      const { loginStore } = getState();
      const { account } = loginStore;
      const currency = getSelectedCurrency(account);
      const url = Urls.InsuranceGetQuote;
      const startDateTime = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null;
      const endDateTime = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null;

      const payload = {
        totalPrice: {
          isoCurrencyCode: currency,
          value: total,
        },
        booking: {
          startDateTime,
          endDateTime,
          destinationIsoCountryCode,
        },
        travelers: {
          numberOfTravelers,
          residency: {
            isoStateOrProvince,
            isoCountry: 'US',
          },
        },
      };

      const { data } = await axiosInstance.post(url, payload, { ...getHeaders() });
      const quote: IQuoteResponse = data;
      dispatch(insuranceQuoteActions.setQuote({ quote }));
      dispatch(insuranceQuoteActions.setQuoteLoading({ loading: false }));
    } catch (err: any) {
      const error = err.response ? JSON.parse(err?.response?.data)?.reason : 'Not Identified Error';
      dispatch(insuranceQuoteActions.setQuoteError({ error }));
      dispatch(insuranceQuoteActions.setQuoteLoading({ loading: false }));
    }
  };
};

export const PostInsuranceCheckout = (
  quoteId: string,
  PolicyBookingId: string,
  users: IUser[],
  card: ICard,
): AppThunk => {
  return async (dispatch, getState) => {
    try {
      dispatch(insuranceQuoteActions.setCheckoutLoading({ loading: true }));
      const url = Urls.InsuranceCheckout;

      const travelers = users.map((u) => ({
        firstName: u?.firstName,
        middleName: u?.middleName,
        lastName: u?.lastName,
        addresses: [
          {
            type: 'Residency',
            street1: u?.street,
            city: u?.city,
            postalCode: u?.postalCode,
          },
        ],
        email: u?.email,
        phone: u?.phone,
      }));

      const paymentDetail = {
        cardDetails: {
          cardholderName: card?.holderName,
          cardholderAddress: {
            type: card?.type,
            isoStateOrProvince: card?.state,
            postalCode: card?.zipCode,
            isoCountry: card?.country,
          },
          cardType: card?.cardType,
          cardNumber: card?.cardNumber,
          expirationDate: card?.expireDate,
          securityCode: card?.cvv,
        },
      };

      /* When the quote came from a booking, identify it the way the in-checkout path does so
         the policy is linked to that booking. Without it the backend has only the typed
         confirmation number and files the policy as its own insurance booking. */
      const { bookingLink } = getState().insuranceQuoteStore;

      const payload = bookingLink
        ? {
            quoteId,
            policyBookingId: bookingLink.policyBookingId,
            bookingInternalId: bookingLink.bookingInternalId,
            Internal: true,
            travelers,
            paymentDetail,
          }
        : {
            quoteId,
            PolicyBookingId,
            travelers,
            paymentDetail,
          };

      const { data } = await axiosInstance.post(url, payload, { ...getHeaders() });
      dispatch(insuranceQuoteActions.setCheckout({ checkout: data?.policyDetail }));
      dispatch(insuranceQuoteActions.setCheckoutLoading({ loading: false }));
    } catch (err: any) {
      const error = err.response ? JSON.parse(err?.response?.data)?.reason : 'Not Identified Error';
      dispatch(insuranceQuoteActions.setCheckoutError({ error }));
      dispatch(insuranceQuoteActions.setCheckoutLoading({ loading: false }));
    }
  };
};
